import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import fetchProductReviews, {RatingValue} from '~/client/spreadshop/old/fetchProductReviews'
import {applyServerFnCache} from '~/client/fn/utils'
import {Locale} from '~/gql/graphql'

const REVIEW_ITEM_LIMIT = 12
const CACHE_TIME_SEC = 60 * 60 // 1h

const reviewsParams = z.object({
    productTypeId: z.string(),
    offset: z.number().optional(),
    limit: z.number().max(REVIEW_ITEM_LIMIT).optional(),
    sizeIds: z.array(z.string()).optional(),
    ratings: z.array(z.nativeEnum(RatingValue)).optional(),
    locale: z.nativeEnum(Locale).optional(),
})

const getProductTypeReviews = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = reviewsParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }
        return result.data
    })
    .handler(async ({data}) => {
        const {productTypeId, offset, limit, sizeIds, ratings, locale} = data

        const productReviews = await fetchProductReviews(
            productTypeId,
            offset,
            limit,
            sizeIds,
            ratings,
            locale
        )

        if (!productReviews) {
            throw notFound()
        }

        applyServerFnCache(CACHE_TIME_SEC)

        return productReviews
    })

export const productTypeReviewsQueryOptions = (
    productTypeId: string,
    page: number = 1,
    sizeIds: string[] | undefined = undefined,
    ratings: RatingValue[] | undefined = undefined,
    locale: Locale = Locale.EnEu
) => {
    return queryOptions({
        queryKey: [locale, 'reviews', productTypeId, page, sizeIds, ratings],
        gcTime: 1000 * CACHE_TIME_SEC, // 1h
        staleTime: 1000 * CACHE_TIME_SEC, // 1h
        queryFn: async () => {
            const offset = Math.max((page - 1) * REVIEW_ITEM_LIMIT, 0)
            const reviews = await getProductTypeReviews({
                data: {productTypeId, offset, limit: REVIEW_ITEM_LIMIT, sizeIds, ratings, locale},
            })

            const pageLimit = Math.ceil(reviews.reviewCount / REVIEW_ITEM_LIMIT)

            return {
                ...reviews,
                hasPreviousPage: page > 1,
                hasNextPage: page < pageLimit,
                pageLimit,
            }
        },
        placeholderData: (previousData) => previousData,
    })
}
